import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

import axios from "axios";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    borderRadius: "10px",
  },
  titleContainer: {
    margin: theme.spacing(1, 0),
  },
  instructionsBox: {
    borderRadius: "10px",
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  accordion: {
    marginBottom: theme.spacing(2),
    border: "none",
  },
  accordionSummary: {
    border: "none",
  },
  formContainer: {
    width: "100%",
  },
  textField: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
  },
  submitButton: {
    borderRadius: "10px",
    position: "relative",
  },
  button: {
    borderRadius: "10px",
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textAccordion: {
    color: theme.palette.primary.main,
  },
  subtitle: {
    marginBottom: theme.spacing(2),
  },
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();

  const [formMessageTextData,] = useState({ token: '', number: '', body: '', userId: '', queueId: '' })
  const [formMessageMediaData,] = useState({ token: '', number: '', medias: '', body: '', userId: '', queueId: '' })
  const [file, setFile] = useState({})
  const { user, socket } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + '/api/messages/send'
  }

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    const data = { number, body, userId, queueId };
    try {
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${values.token}`
        }
      })
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  }

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append('number', values.number);
      data.append('body', values.body ? values.body : firstFile.name);
      data.append('userId', values.userId);
      data.append('queueId', values.queueId);
      data.append('medias', firstFile);
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'multipart/form-data',
          'Authorization': `Bearer ${values.token}`
        }
      })
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  }

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.button}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
            document.getElementById('medias').files = null
            document.getElementById('medias').value = null
          }, 400);
        }}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  size="small"
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <input type="file" name="medias" id="medias" required onChange={(e) => setFile(e.target.files)} />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className={classes.button}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <MainContainer>
      <MainHeader>
        <div className={classes.titleContainer}>
          <Title>{i18n.t("API de Mensagens")}</Title>
        </div>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <Box className={classes.instructionsBox}>
          <Typography variant="h5">
            {i18n.t("messagesAPI.API.title")}
          </Typography>
          <Typography variant="h6" color="primary">
            {i18n.t("messagesAPI.API.methods.title")}
          </Typography>
          <Typography component="div">
            <ol>
              <li>{i18n.t("messagesAPI.API.methods.messagesText")}</li>
              <li>{i18n.t("messagesAPI.API.methods.messagesMidia")}</li>
            </ol>
          </Typography>
          <Typography variant="h6" color="primary">
            {i18n.t("messagesAPI.API.instructions.title")}
          </Typography>
          <Typography component="div">
            <b>{i18n.t("messagesAPI.API.instructions.comments")}</b><br />
            <ul>
              <li>{i18n.t("messagesAPI.API.instructions.comments1")}</li>
              <li>
                {i18n.t("messagesAPI.API.instructions.comments2")}
                <ul>
                  <li>{i18n.t("messagesAPI.API.instructions.codeCountry")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.code")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.number")}</li>
                </ul>
              </li>
            </ul>
          </Typography>
        </Box>

        <Box className={classes.instructionsBox}>
          <Typography className={classes.textAccordion} variant="h6" gutterBottom>
            {i18n.t("messagesAPI.API.methods.title")}
          </Typography>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography>{i18n.t("messagesAPI.API.methods.messagesText")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" component="div">
                    <p>{i18n.t("messagesAPI.API.text.instructions")}</p>
                    <b>Endpoint: </b> {getEndpoint()} <br />
                    <b>Método: </b> POST <br />
                    <b>Headers: </b> Authorization Bearer (token registrado) e Content-Type (application/json) <br />
                    <b>Body: </b> {"{"} <br></br>
                    "number": "558599999999" <br></br>
                    "body": "Message" <br></br>
                    "userId": ID usuário ou "" <br></br>
                    "queueId": ID Fila ou ""<br></br>
                    "sendSignature": Assinar mensagem - true/false <br></br>
                    "closeTicket": Encerrar o ticket - true/false<br></br>
                    {"}"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>
                    Teste de Envio
                  </Typography>
                  {renderFormMessageText()}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography>{i18n.t("messagesAPI.API.media.title")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" component="div">
                    <p>{i18n.t("messagesAPI.API.media.instructions")}</p>
                    <b>Endpoint: </b> {getEndpoint()} <br />
                    <b>Método: </b> POST <br />
                    <b>Headers: </b> Authorization Bearer (token cadastrado) e Content-Type (multipart/form-data) <br />
                    <b>FormData: </b> <br />
                    <ul>
                      <li>
                        <b>number: </b> 558599999999
                      </li>
                      <li>
                        <b>body:</b> Message
                      </li>
                      <li>
                        <b>userId:</b> ID usuário ou ""
                      </li>
                      <li>
                        <b>queueId:</b> ID da fila ou ""
                      </li>
                      <li>
                        <b>medias: </b> arquivo
                      </li>
                      <li>
                        <b>sendSignature:</b> Assinar mensagem true/false
                      </li>
                      <li>
                        <b>closeTicket:</b> Encerrar ticket true/false
                      </li>
                    </ul>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6" gutterBottom>
                    Teste de Envio
                  </Typography>
                  {renderFormMessageMedia()}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default MessagesAPI;
